import { isPlatformServer } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Inject, Optional, PLATFORM_ID } from '@angular/core';
import { TranslateLoader } from '@ngx-translate/core';
import { forkJoin, iif, Observable, of } from 'rxjs';
import { catchError, first, map, retryWhen, switchMap } from 'rxjs/operators';
import { LOCALIZATION_DATA_TOKEN } from '../../../../../localization-data-token';
import { AbstractNewRelicService } from '../../lib/services/abstract-new-relic.service';
import { ClientConfigService } from '../../lib/services/client-config/client-config.service';
import { genericRetryStrategy } from '../../lib/utils/generic-retry-strategy';
import { LocalizationData } from './localization-data';

export class CustomTranslateLoader implements TranslateLoader {
  constructor(
    private readonly httpClient: HttpClient,
    private readonly clientConfigService: ClientConfigService,
    private readonly newRelicService: AbstractNewRelicService,
    @Optional() @Inject(LOCALIZATION_DATA_TOKEN) private readonly localizationData: () => LocalizationData,
    @Inject(PLATFORM_ID) private readonly platformId
  ) {}

  // eslint-disable-next-line
  public getTranslation(lang: 'en' | 'ar'): Observable<any> {
    const browserLocalizationDataSource$: Observable<[object, object, object]> = this.clientConfigService.configAvailable$.pipe(
      first((v) => !!v),
      switchMap(() => {
        const baseUrl = new URL(this.clientConfigService.localizationBaseUrl);
        const normalizedBasePath =
          baseUrl.pathname && baseUrl.pathname[baseUrl.pathname.length - 1] === '/' ? baseUrl.pathname : `${baseUrl.pathname}/`;
        const apiAddress = new URL(`${normalizedBasePath}${lang}.json`, baseUrl.origin).href;

        return forkJoin([
          this.httpClient.get(apiAddress).pipe(
            retryWhen(genericRetryStrategy({ maxRetryAttempts: 2, scalingDuration: 200 })),
            catchError((error) => {
              this.newRelicService.noticeError(error);
              return of({});
            })
          ),
          this.httpClient.get(`/assets/i18n/${lang}.json`),
          lang !== 'en' ? this.httpClient.get(`/assets/i18n/en.json`) : of(undefined),
        ]);
      })
    );

    const serverLocalizationDataSource$: Observable<[object, object, object]> = of(this.localizationData?.()).pipe(
      map((result) => [result?.[lang].remote, result?.[lang].local, result?.en.local])
    );

    return iif(() => isPlatformServer(this.platformId), serverLocalizationDataSource$, browserLocalizationDataSource$).pipe(
      map(([remote, local, english]) => this.mapLocalizationData(remote, local, lang, lang !== 'en' ? english : local)),
      catchError((error) => {
        console.warn('i18n initialization error');
        console.warn(error);
        return of({});
      })
    );
  }

  private mapLocalizationData(remoteDictionary, localDictionary, languageCode, english) {
    const localKeys = Object.keys(localDictionary);

    if (!remoteDictionary || Object.keys(remoteDictionary).length === 0 || typeof remoteDictionary !== 'object') {
      console.warn(`Missing remote localization in language '${languageCode}'`);
      return localDictionary;
    }

    remoteDictionary = JSON.parse(JSON.stringify(remoteDictionary));

    const missingRemoteKeys = localKeys.filter((key) => !Object.keys(remoteDictionary).includes(key));

    console.warn(
      `Missing the following remote localization keys in language '${languageCode}':\n${missingRemoteKeys
        .map((k) => `${k} - ${english[k]}`)
        .join('\n')}`
    );

    for (const translationKey of Object.keys(remoteDictionary)) {
      remoteDictionary[translationKey] = remoteDictionary[translationKey].replace(/{/g, '{{');
      remoteDictionary[translationKey] = remoteDictionary[translationKey].replace(/}/g, '}}');
    }

    return {
      ...localDictionary,
      ...remoteDictionary,
    };
  }
}
